import React, { useCallback, useState } from 'react';
import { isFunction } from 'lodash';

import useToggle from '@lyearn/core/hooks/useToggle';
import { Trans, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import {
  Box,
  Button,
  LinkButton,
  Popover,
  PopperProps,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from '@lyearn/ui';
import { CONTENT_TYPE_LOWER_CASE_i18n_CONFIG } from '@/modules/content/config';
import { useFetchIsPublishableQuery } from '@/modules/content/graphql/queries/generated/fetchIsPublishable';
import { useGetParentContentsCountQuery } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/queries/generated/getParentContentsCount';
import {
  ContentEntity,
  PublishContentConfig,
  ResourceTypeEnum,
  ResourceVersionTypeEnum,
} from '@/types/schema';

import { onPublishType } from '../../../types';
import SharePublicly from '../SharePublicly';
import usePublishChanges from '../usePublishChanges';
import ImpactedItemsDialog from './ImpactedItemsDialog';
import PublishOptions from './PublishOptions';
import { PublishChangesTab } from './utils';

interface ButtonParams {
  publishConfig: PublishContentConfig;
}

type Props = {
  onClose: () => void;
  onPublish: onPublishType;
  open: boolean;
  children: ((props: ButtonParams) => React.ReactNode) | React.ReactNode;
  contentType?: ResourceTypeEnum;
  disabled?: boolean;
  allowMinorVersionPublish?: boolean;
  id?: string;
  allBlocksNotTranslated?: boolean;
};

export const POPOVER_CLASSES = {
  Popover: {
    // Sit at the modal layer (MUI theme.zIndex.modal = 1300) rather than a low z so the
    // popover is visible when the editor is rendered inside a Dialog (e.g. the embedded
    // course-builder exercise/quiz/survey editors) instead of being painted behind it.
    // It is kept *equal* to the modal layer — not above — so DOM/mount order still lets a
    // dialog opened from within the popover (e.g. ImpactedItemsDialog) stack on top of it.
    paper: 'z-[1300] !mt-8 rounded-16 w-[44rem] p-24',
  },
};

export const POPPER_PROPS: Partial<PopperProps> = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [-100, 0],
      },
    },
  ],
} as any;

const PublishChangesPopover: React.FC<Props> = ({
  open,
  onClose,
  onPublish,
  allowMinorVersionPublish: conditionalAllowMinorVersionPublish,
  children,
  contentType,
  disabled,
  id,
  allBlocksNotTranslated = false,
}) => {
  const { t } = useTranslation();
  const [showImpactedItemDialog, { toggle: toggleImpactedItemDialog }] = useToggle();
  const [currentTab, setCurrentTab] = useState<PublishChangesTab>(PublishChangesTab.Publish);

  const fetchedAllowMinorVersionPublish =
    (
      useFetchIsPublishableQuery({
        variables: { filters: { id: id ?? '' } },
        pause: !id,
      })[0].data?.content as ContentEntity
    )?.allowMinorVersionPublish ?? true;

  const allowMinorVersionPublish =
    conditionalAllowMinorVersionPublish != undefined
      ? conditionalAllowMinorVersionPublish && fetchedAllowMinorVersionPublish
      : fetchedAllowMinorVersionPublish;

  const isLearnerProgessAffected = contentType
    ? [
        ResourceTypeEnum.Course,
        ResourceTypeEnum.Article,
        ResourceTypeEnum.Task,
        ResourceTypeEnum.Path,
        ResourceTypeEnum.Quiz,
        ResourceTypeEnum.ExternalContent,
      ].includes(contentType)
    : false;
  const {
    hasError,
    onNoteTextChange,
    publishOptions,
    onRecalibrateChange,
    onVersionChange,
    publishConfig,
    onPublishChanges,
  } = usePublishChanges(
    onPublish,
    onClose,
    isLearnerProgessAffected,
    allowMinorVersionPublish,
    contentType,
  );

  const onOpenChange = useCallback(
    (val: boolean) => {
      if (!val) {
        onClose();
      }
    },
    [onClose],
  );

  const count = useGetParentContentsCountQuery({
    variables: {
      filters: {
        id: id ? id : '',
      },
    },
    pause: !id || !isLearnerProgessAffected,
  })[0].data?.parentContents?.totalCount;

  const renderDescription = () => {
    switch (contentType) {
      case ResourceTypeEnum.Scorm:
      case ResourceTypeEnum.Survey:
      case ResourceTypeEnum.Exercise:
        return (
          <Box className="mt-12 pt-12 pl-12 pr-16 pb-16 bg-bg-secondary rounded-8">
            <Typography color="text-text-secondary" variant="body-short-02">
              <Trans
                i18nKey="Admin:Contents.PublishPrompt"
                values={{
                  contentType: t(CONTENT_TYPE_LOWER_CASE_i18n_CONFIG[contentType]),
                }}
              />
            </Typography>
          </Box>
        );

      default:
        return (
          <>
            {count ? (
              <Box className="mt-12 pt-12 pl-12 pr-16 pb-16 bg-bg-secondary rounded-8">
                <Typography color="text-text-secondary" variant="body-short-02">
                  <Trans
                    i18nKey="Admin:Contents.MustPublishNewVersionDescription"
                    values={{ count: count }}
                  />
                </Typography>
                <Stack>
                  <Typography color="text-text-secondary" variant="label-02">
                    <Trans
                      i18nKey="Admin:Contents.MustPublishNewVersionDescriptionCount"
                      values={{ count: count }}
                    />
                  </Typography>
                  <LinkButton
                    className="ml-2 text-link-internal"
                    onClick={toggleImpactedItemDialog}>
                    {t('Common:ViewItem', { count })}
                  </LinkButton>
                </Stack>
              </Box>
            ) : null}
          </>
        );
    }
  };

  const isPublishView = currentTab === PublishChangesTab.Publish;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Button
        className={cn(allBlocksNotTranslated ? '!pointer-events-auto' : '')}
        disabled={disabled || allBlocksNotTranslated}>
        {allBlocksNotTranslated ? (
          <Tooltip PopperProps={POPPER_PROPS} title={t('Course:MultilingualPublishDisabled')}>
            {(isFunction(children) ? children({ publishConfig }) : children) as React.ReactElement}
          </Tooltip>
        ) : isFunction(children) ? (
          children({ publishConfig })
        ) : (
          children
        )}
      </Popover.Button>
      <Popover.Content classes={POPOVER_CLASSES} position="bottom-start" renderAs="popper">
        <Stack direction="col">
          <Stack className="mb-24" direction="col">
            <Stack direction="col" gapY={12}>
              {/* <Tabs
                className="ms-4"
                tabs={getTabs(t, contentType)}
                value={currentTab}
                variant="small"
                onChange={(val) => setCurrentTab(val)}
              /> */}
              {isPublishView && (
                <Textarea
                  characterLimit={140}
                  className="h-[12rem] border-transparent bg-bg-tertiary"
                  error={hasError}
                  placeholder={t('Common:Form.VersionNotePlaceholder')}
                  value={publishConfig.note}
                  onChangeText={onNoteTextChange}
                />
              )}
            </Stack>
            {hasError && isPublishView ? (
              <Typography className="mt-8 px-8" color="text-text-error" variant="body-short-03">
                {t('Common:Form.Error.VersionNoteCantBeEmpty')}
              </Typography>
            ) : null}
          </Stack>
          {isPublishView ? (
            <>
              <PublishOptions
                allowMinorVersionPublish={allowMinorVersionPublish}
                contentType={contentType}
                publishConfig={publishConfig}
                publishOptions={publishOptions}
                onRecalibrateChange={onRecalibrateChange}
                onVersionChange={onVersionChange}
              />
              {publishConfig.versionType === ResourceVersionTypeEnum.Major && renderDescription()}
              {showImpactedItemDialog && id && contentType ? (
                <ImpactedItemsDialog
                  contentType={contentType}
                  id={id}
                  onClose={toggleImpactedItemDialog}
                />
              ) : null}
              <Button
                fullWidth
                className="mt-28"
                shape="square"
                size="large"
                onClick={onPublishChanges}>
                {t('Common:Actions.Publish')}
              </Button>
            </>
          ) : (
            <SharePublicly entityId={id} />
          )}
        </Stack>
      </Popover.Content>
    </Popover>
  );
};

export default React.memo(PublishChangesPopover);
